import React, { useState } from 'react';
import PageHeader from '../components/shared/PageHeader';
import AnimatedSection from '../components/ui/AnimatedSection';
import { Phone, Mail, MapPin, ArrowLeft, ArrowRight, ExternalLink } from 'react-feather';
import Button from '../components/ui/Button';
import FlippableCard from '../components/ui/FlippableCard';
import PageExtras from '../components/shared/PageExtras';
import { trackEvent, trackConversion, generateConversionEventId } from '../services/trackingService';
import Seo from '../components/shared/Seo';
import { submitContactForm } from '../services/apiService';
import type { LeadDocketCaseType } from '../types';
import { useUtm } from '../hooks/useUtm';
import { useData } from '../context/DataContext';
import { useTranslation } from 'react-i18next';

// DEFINITIVE FIX: Use a static, hardcoded list of case types based on the user's CRM specification.
// This removes the dependency on a live API call to render the form, making the page faster,
// more reliable, and fixing the bug where the dropdown only showed "Other" due to API failures.
const personalInjuryCaseTypes: LeadDocketCaseType[] = [
    { Id: 1, TypeName: "Motor Vehicle Accident" },
    { Id: 6, TypeName: "Trucking Accident" },
    { Id: 8, TypeName: "Motorcycle Accident" },
    { Id: 5, TypeName: "Slip And Fall / Premises Liability" },
    { Id: 4, TypeName: "Pedestrian / Crosswalk Accident" },
    { Id: 10, TypeName: "Wrongful Death" },
    { Id: 7, TypeName: "Dog / Animal Bite" },
    { Id: 11, TypeName: "Rideshare" },
    { Id: 9, TypeName: "eScooter / eBike / Bicycle Accident" },
    { Id: 2, TypeName: "Other" },
];


const ContactPage: React.FC = () => {
    const { t } = useTranslation('common');
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const { firmInfo } = useData();

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        caseType: personalInjuryCaseTypes[0].TypeName, // Initialize with the first valid type
        incidentDescription: '',
        incidentDate: '',
    });

    const { getUtmFields } = useUtm();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};
        if (step === 1) {
            if (!formData.name.trim()) newErrors.name = t('contactPage.fullNameError');
            if (!formData.incidentDate) newErrors.incidentDate = t('contactPage.incidentDateError');
            if (!formData.caseType) newErrors.caseType = t('contactPage.caseTypeError');
        }
        if (step === 2) {
            if (!formData.phone.trim()) {
                newErrors.phone = t('contactPage.phoneError');
            } else if (!/^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/.test(formData.phone)) {
                newErrors.phone = t('contactPage.phoneInvalidError');
            }
            if (!formData.email.trim()) {
                newErrors.email = t('contactPage.emailError');
            } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = t('contactPage.emailInvalidError');
            }
        }
        if (step === 3) {
            if (!formData.incidentDescription.trim()) newErrors.incidentDescription = t('contactPage.descriptionError');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
        }
    };
    
    const handlePrevStep = () => {
        setCurrentStep(prev => prev - 1);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateStep(3)) return;

        setIsSubmitting(true);
        setSubmitStatus('idle');

        const utmFields = getUtmFields();
        // Generated before submit so the SAME id reaches both the server
        // (for a future CAPI send, once wired) and the browser pixel call
        // below -- required for real client/server event dedup per the
        // firm's own Meta advertising research.
        const fbEventId = generateConversionEventId('Lead');
        const submissionData = { ...formData, ...utmFields, fbEventId };

        try {
            await submitContactForm(submissionData);
            setSubmitStatus('success');
            trackEvent('contact_form_submit');
            trackConversion('Lead', { content_name: 'contact_form', content_category: submissionData.caseType || 'general' }, fbEventId);
        } catch (error) {
            console.error("Form submission error:", error);
            setSubmitStatus('error');
            trackEvent('form_submission_error', { error: (error as Error).message });
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = (fieldName: string) => 
        `w-full bg-white/5 border ${errors[fieldName] ? 'border-red-500' : 'border-white/10'} rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-brand-gold`;
    
    const ProgressBar = () => {
        const steps = [t('contactPage.formStep1'), t('contactPage.formStep2'), t('contactPage.formStep3')];
        return (
            <div className="flex items-center mb-12">
                {steps.map((step, index) => {
                    const stepNumber = index + 1;
                    const isCompleted = currentStep > stepNumber;
                    const isActive = currentStep === stepNumber;
                    return (
                        <React.Fragment key={step}>
                            <div className="flex flex-col items-center text-center">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${isCompleted ? 'gold-gradient-bg text-brand-dark' : isActive ? 'bg-brand-gold-light text-brand-dark' : 'border-2 border-white/20 text-white'}`}>
                                    {stepNumber}
                                </div>
                                <p className={`mt-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${isCompleted || isActive ? 'text-brand-gold-light' : 'text-white/50'}`}>{step}</p>
                            </div>
                            {index < steps.length - 1 && (
                                <div className="flex-1 h-1 mx-2 bg-white/10 rounded-full overflow-hidden">
                                     <div className="h-full bg-brand-gold-light transition-all duration-500" style={{ width: isCompleted ? '100%' : isActive ? '50%' : '0%' }} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <Seo
                title={t('contactPage.seoTitle')}
                description={t('contactPage.seoDescription')}
            />
            <div>
                <PageHeader
                    title={t('contactPage.pageTitle')}
                    subtitle={t('contactPage.pageSubtitle')}
                    imageUrl="https://images.pexels.com/photos/8112166/pexels-photo-8112166.jpeg?auto=compress&cs=tinysrgb&w=1600"
                />
                
                <div className="py-24 dark-section-bg">
                    <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-5 gap-16">
                        <AnimatedSection className="lg:col-span-2">
                            <h2 className="text-3xl font-bold font-serif">{t('contactPage.asideTitle')}</h2>
                            <p className="mt-4 text-lg text-white/80">
                                {t('contactPage.asideParagraph1')}
                            </p>
                            <p className="mt-4 text-lg text-white/80">
                                {t('contactPage.asideParagraph2')}
                            </p>
                            <div className="mt-10 space-y-6">
                                {[
                                    { icon: <Phone />, title: t('contactPage.callUs'), value: firmInfo.displayPhone, href: `tel:${firmInfo.telephone}` },
                                    { icon: <Mail />, title: t('contactPage.emailUs'), value: "intake@theactionfirm.law", href: `mailto:intake@theactionfirm.law` },
                                    { icon: <MapPin />, title: t('contactPage.visitUs'), value: `${firmInfo.address.streetAddress}, ${firmInfo.address.addressLocality}, UT`, href: firmInfo.googleMapsUrl, target: "_blank" }
                                ].map(item => (
                                    <a key={item.title} href={item.href} target={item.target} rel="noopener noreferrer" className="flex items-center gap-4 group p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 transform hover:scale-105">
                                        <div className="w-12 h-12 rounded-xl bg-brand-dark/50 flex items-center justify-center text-brand-gold-light transition-colors flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-white/90">{item.title}</h3>
                                            <p className="text-brand-gold-light text-lg">{item.value}</p>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </AnimatedSection>

                        <AnimatedSection className="lg:col-span-3">
                            <div className="p-8 md:p-12 rounded-3xl border border-white/10 bg-white/5 glass-effect shadow-card">
                                {submitStatus === 'success' ? (
                                    <div className="text-center py-12">
                                        <h3 className="text-2xl font-bold font-serif text-brand-gold-light">{t('contactPage.successTitle')}</h3>
                                        <p className="mt-2 text-white/90">{t('contactPage.successMessage')}</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} noValidate>
                                        <ProgressBar />
                                        
                                        {currentStep === 1 && (
                                            <div className="space-y-6">
                                                <div>
                                                    <label htmlFor="name" className="block text-sm font-medium mb-1">{t('contactPage.fullNameLabel')}</label>
                                                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className={inputClass('name')} required />
                                                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                                </div>
                                                <div>
                                                     <label htmlFor="incidentDate" className="block text-sm font-medium mb-1">{t('contactPage.incidentDateLabel')}</label>
                                                     <input type="date" name="incidentDate" id="incidentDate" value={formData.incidentDate} onChange={handleChange} className={inputClass('incidentDate')} required />
                                                     {errors.incidentDate && <p className="text-red-500 text-xs mt-1">{errors.incidentDate}</p>}
                                                </div>
                                                <div>
                                                    <label htmlFor="caseType" className="block text-sm font-medium mb-1">{t('contactPage.caseTypeLabel')}</label>
                                                    <select name="caseType" id="caseType" value={formData.caseType} onChange={handleChange} className={`${inputClass('caseType')} appearance-none`} required>
                                                        {personalInjuryCaseTypes.map(ct => <option key={ct.Id} value={ct.TypeName}>{ct.TypeName}</option>)}
                                                    </select>
                                                    {errors.caseType && <p className="text-red-500 text-xs mt-1">{errors.caseType}</p>}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {currentStep === 2 && (
                                            <div className="space-y-6">
                                                <div>
                                                    <label htmlFor="phone" className="block text-sm font-medium mb-1">{t('contactPage.phoneLabel')}</label>
                                                    <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} className={inputClass('phone')} required />
                                                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                                                </div>
                                                <div>
                                                    <label htmlFor="email" className="block text-sm font-medium mb-1">{t('contactPage.emailLabel')}</label>
                                                    <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} className={inputClass('email')} required />
                                                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                                </div>
                                            </div>
                                        )}

                                        {currentStep === 3 && (
                                            <div className="space-y-6">
                                                <div>
                                                    <label htmlFor="incidentDescription" className="block text-sm font-medium mb-1">{t('contactPage.descriptionLabel')}</label>
                                                    <textarea name="incidentDescription" id="incidentDescription" rows={8} value={formData.incidentDescription} onChange={handleChange} className={inputClass('incidentDescription')} required />
                                                    {errors.incidentDescription && <p className="text-red-500 text-xs mt-1">{errors.incidentDescription}</p>}
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-8 flex justify-between items-center">
                                            {currentStep > 1 ? (
                                                <Button type="button" variant="ghost" onClick={handlePrevStep} icon={<ArrowLeft size={16} />}>
                                                    {t('buttons.previous')}
                                                </Button>
                                            ) : <div />}
                                            
                                            {currentStep < 3 ? (
                                                <Button type="button" variant="secondary" onClick={handleNextStep}>
                                                    {t('buttons.nextStep')} <ArrowRight size={16} className="ml-1" />
                                                </Button>
                                            ) : (
                                                <Button type="submit" variant="primary" className="w-full md:w-auto" disabled={isSubmitting}>
                                                    {isSubmitting ? t('buttons.sending') : t('buttons.sendMyFreeCaseReview')}
                                                </Button>
                                            )}
                                        </div>
                                        
                                        {submitStatus === 'error' && <p className="text-red-500 text-center text-sm mt-4">{t('contactPage.submitError')}</p>}
                                        
                                        <p className="mt-8 text-xs text-center text-white/50">
                                            {t('contactPage.formDisclaimer')}
                                        </p>
                                    </form>
                                )}
                            </div>
                        </AnimatedSection>
                    </div>
                </div>

                <AnimatedSection className="py-24">
                    <div className="mx-auto max-w-7xl px-6">
                        <div className="text-center">
                            <h2 className="text-3xl font-bold font-serif">{t('contactPage.locationTitle')}</h2>
                            <p className="mt-2 text-lg text-white/70 max-w-2xl mx-auto">{t('contactPage.locationSubtitle')}</p>
                        </div>
                        {/* 
                          PERFORMANCE OPTIMIZATION: 
                          This div serves as a container for the Google Map, which is dynamically injected via Google Tag Manager.
                          This prevents the map iframe from loading on the initial page render, significantly improving
                          page load speed and Core Web Vitals scores. The ID 'gtm-map-container' is the specific target for the GTM tag.
                        */}
                        <div 
                          id="gtm-map-container"
                          className="mt-12 aspect-video w-full rounded-3xl border border-white/10 shadow-card overflow-hidden bg-white/5 flex items-center justify-center"
                        >
                           {/* Placeholder content until GTM injects the map */}
                           <div className="text-center text-white/70">
                                <MapPin size={32} className="mx-auto mb-2" />
                                <p>{t('contactPage.mapLoading')}</p>
                           </div>
                        </div>

                        <div className="mt-16 text-center">
                            <h3 className="text-2xl font-bold font-serif mb-4">{t('contactPage.courthouseTitle')}</h3>
                            <p className="text-white/70 mb-6">{t('contactPage.courthouseSubtitle')}</p>
                            <div className="grid md:grid-cols-3 gap-6">
                                {(t('contactPage.courthouseList', { returnObjects: true }) as Array<{name: string, district: string, location: string, address: string, phone: string, url: string, directionsUrl: string}>).map((courthouse, index) => (
                                    <FlippableCard
                                        key={index}
                                        frontContent={
                                            <div className="flex flex-col items-center justify-center h-full min-h-[280px] p-6 text-center bg-white/5 border border-white/10 rounded-2xl shadow-sm">
                                                <h4 className="text-xl font-bold font-serif text-brand-gold-light mb-2">{courthouse.name}</h4>
                                                <p className="text-white/80 font-medium">{courthouse.district}</p>
                                                <div className="mt-4 flex items-center text-white/60">
                                                    <MapPin size={16} className="mr-2" />
                                                    <span>{courthouse.location}</span>
                                                </div>
                                                <p className="mt-6 text-xs text-white/40 uppercase tracking-wider">Hover for details</p>
                                            </div>
                                        }
                                        backContent={
                                            <div className="flex flex-col items-center justify-center h-full p-6 text-center bg-brand-dark border border-brand-gold/30 rounded-2xl shadow-lg">
                                                <p className="text-sm text-white/90 mb-4">{courthouse.address}</p>
                                                <a href={`tel:${courthouse.phone.replace(/[^0-9]/g, '')}`} className="flex items-center text-brand-gold hover:text-brand-gold-light transition-colors mb-6 font-bold">
                                                    <Phone size={16} className="mr-2" />
                                                    {courthouse.phone}
                                                </a>
                                                <div className="flex flex-col gap-3 w-full">
                                                    <a href={courthouse.directionsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full py-2 px-4 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors">
                                                        <MapPin size={14} className="mr-2" />
                                                        Get Directions
                                                    </a>
                                                    <a href={courthouse.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full py-2 px-4 bg-brand-gold/20 hover:bg-brand-gold/30 text-brand-gold-light rounded-lg text-sm transition-colors">
                                                        <ExternalLink size={14} className="mr-2" />
                                                        Court Website
                                                    </a>
                                                </div>
                                            </div>
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </AnimatedSection>
                <PageExtras pageKey="contact" />
            </div>
        </>
    );
};

export default ContactPage;